import { z } from "zod";

/**
 * The credit tiers, as the Checkout board prices them. One credit is $0.01: Starter 5,000 for
 * $50, Team 20,000 for $190 (save 5%), Scale 100,000 for $900 (save 10%). Defined once here so
 * the pack picker, the order summary, and the API cannot drift.
 */
export const creditPacks = ["starter", "team", "scale"] as const;
export const creditPackSchema = z.enum(creditPacks);
export type CreditPack = z.infer<typeof creditPackSchema>;

export const CREDIT_PACKS: Record<CreditPack, { credits: number; priceCents: number }> = {
  starter: { credits: 5_000, priceCents: 5_000 },
  team: { credits: 20_000, priceCents: 19_000 },
  scale: { credits: 100_000, priceCents: 90_000 },
};

/** VAT at 20%, as the order summary shows it. */
export const VAT_RATE_PERCENT = 20;

/**
 * The seven payment states, as the CheckoutStates board draws them. A payment is never shown as
 * done until the provider's signed confirmation is verified. `refunded` is terminal: money went
 * back through the provider and the wallet holds a matching reversal. `member-view` is not a
 * payment state but the screen a non-owner sees, kept here so the page cannot invent a seventh.
 */
export const paymentStates = [
  "pending",
  "needs-authentication",
  "confirmed",
  "declined",
  "unknown",
  "refunded",
  "member-view",
] as const;
export const paymentStateSchema = z.enum(paymentStates);
export type PaymentState = z.infer<typeof paymentStateSchema>;

/**
 * One payment, as the API returns it. The card number never reaches the API: only the brand,
 * the last four digits, and the expiry are stored. The provider event id makes the webhook
 * idempotent, so a replay never credits twice.
 */
export const paymentSchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  pack: creditPackSchema,
  credits: z.number().int().min(1),
  amountCents: z.number().int().min(1),
  status: paymentStateSchema,
  cardBrand: z.string().min(1),
  cardLastFour: z.string().regex(/^[0-9]{4}$/),
  cardExpiry: z.string().regex(/^(0[1-9]|1[0-2])\/[0-9]{4}$/),
  providerEventId: z.string().nullable(),
  createdAt: z.iso.datetime(),
});
export type Payment = z.infer<typeof paymentSchema>;

/** `POST /api/v1/projects/:id/checkout`. Only an owner may call it. */
export const createCheckoutSchema = z.object({
  pack: creditPackSchema,
});
export type CreateCheckout = z.infer<typeof createCheckoutSchema>;

/**
 * `POST /api/v1/projects/:id/checkout/session`. Only an owner may call it. Creates a pending
 * payment and returns it with the hosted session URL. The provider opens at that URL; it never
 * renders inside the console. Test mode answers with a local stub URL; live stays unavailable
 * until a real provider is wired.
 */
export const createCheckoutSessionSchema = z.object({
  pack: creditPackSchema,
});
export type CreateCheckoutSession = z.infer<typeof createCheckoutSessionSchema>;

/** One hosted checkout session, as the API returns it. The card number never reaches the API. */
export const checkoutSessionSchema = z.object({
  payment: paymentSchema,
  checkoutUrl: z.url(),
  /** Opaque hosted session reference. Never returned to the browser or written to logs. */
  providerSessionId: z.string().min(1),
  /** True while the stub stands in: no money can move. Live checkout stays unavailable. */
  testMode: z.boolean(),
});
export type CheckoutSession = z.infer<typeof checkoutSessionSchema>;

/**
 * `POST /api/v1/billing/webhook`. Carries the provider event id for idempotency and the signed
 * confirmation the API verifies before crediting. Card details never appear here. A `refunded`
 * event reverses one earlier confirmed purchase with its own event id, so a replay never
 * reverses twice.
 */
export const billingWebhookSchema = z.object({
  providerEventId: z.string().min(1),
  paymentId: z.uuid(),
  status: z.enum(["confirmed", "declined", "unknown", "refunded"]),
  signature: z.string().min(1),
});
export type BillingWebhook = z.infer<typeof billingWebhookSchema>;
